import React, { useEffect } from "react";
import {
  ORGANIZATION_ID,
  buildInstantDownloadShippingDetails,
  buildReturnPolicyReference,
} from "./../../components/SchemaHelpers.js";

const ProductSchemaMarkup = ({ productData }) => {
  useEffect(() => {
    if (!productData) return;

    const productUrl = `https://parishaudio.com/products/${productData.productURI}`;
    const productId = `${productUrl}#product`;
    const offerId = `${productUrl}#offer`;
    const appId = `${productUrl}#app`;
    const breadcrumbId = `${productUrl}#breadcrumb`;

    const imageUrls = [
      productData.googleMarketplaceImage
        ? `https://parishaudio.com${productData.googleMarketplaceImage}`
        : null,
      ...(productData.images || [])
        .filter((img) => img && img.URI)
        .map((img) => `https://parishaudio.com${img.URI}`),
    ].filter(Boolean);

    const uniqueImageUrls = [...new Set(imageUrls)];

    const bulletPoints = [
      productData.bulletPoint1,
      productData.bulletPoint2,
      productData.bulletPoint3,
      productData.bulletPoint4,
      productData.bulletPoint5,
    ].filter(Boolean);

    const controls = (productData.controls || []).map((control) => ({
      "@type": "PropertyValue",
      name: control.name,
      value: control.description,
    }));

    const additionalProperties = [
      ...bulletPoints.map((point, index) => ({
        "@type": "PropertyValue",
        name: `Feature ${index + 1}`,
        value: point,
      })),
      ...controls,
      ...(productData.windowsDownloadURI
        ? [
            {
              "@type": "PropertyValue",
              name: "Windows Download URL",
              value: `https://parishaudio.com${productData.windowsDownloadURI}`,
            },
          ]
        : []),
      ...(productData.macDownloadURI
        ? [
            {
              "@type": "PropertyValue",
              name: "Mac Download URL",
              value: `https://parishaudio.com${productData.macDownloadURI}`,
            },
          ]
        : []),
      {
        "@type": "PropertyValue",
        name: "Plugin Format",
        value: "VST3",
      },
    ];

    const price = Number(productData.price || 0).toFixed(2);
    const msrp =
      productData.msrp !== undefined &&
      productData.msrp !== null &&
      productData.msrp !== ""
        ? Number(productData.msrp).toFixed(2)
        : null;

    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": productId,
      name: productData.name,
      description:
        productData.longDescription ||
        productData.shortDescription ||
        productData.cardSummary ||
        undefined,
      image: uniqueImageUrls.length > 0 ? uniqueImageUrls : undefined,
      sku: productData.SKU || undefined,
      category: "Audio Plugin",
      url: productUrl,
      brand: {
        "@type": "Brand",
        name: "Parish Audio",
      },
      manufacturer: {
        "@type": "Organization",
        "@id": ORGANIZATION_ID,
        name: "Parish Audio",
        url: "https://parishaudio.com",
      },
      isRelatedTo: {
        "@id": appId,
      },
      additionalProperty:
        additionalProperties.length > 0 ? additionalProperties : undefined,
      offers: {
        "@type": "Offer",
        "@id": offerId,
        url: productUrl,
        priceCurrency: "USD",
        price,
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
        seller: {
          "@type": "Organization",
          "@id": ORGANIZATION_ID,
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        hasMerchantReturnPolicy: buildReturnPolicyReference(),
        shippingDetails: buildInstantDownloadShippingDetails("USD"),
        // ...(msrp && msrp !== price
        //   ? {
        //       priceSpecification: {
        //         "@type": "UnitPriceSpecification",
        //         priceCurrency: "USD",
        //         price: msrp,
        //         priceType: "https://schema.org/StrikethroughPrice",
        //       },
        //     }
        //   : {}),
      },
      aggregateRating:
        Number(productData.ratingCount) > 0
          ? {
              "@type": "AggregateRating",
              ratingValue: Number(productData.starRating),
              reviewCount: Number(productData.ratingCount),
              bestRating: 5,
              worstRating: 1,
            }
          : undefined,
    };

    const softwareApplicationSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "@id": appId,
      name: productData.name,
      description:
        productData.longDescription ||
        productData.shortDescription ||
        productData.cardSummary ||
        undefined,
      applicationCategory: "MultimediaApplication",
      operatingSystem: ["Windows", "macOS"],
      softwareVersion: productData.productVersion || "1.0",
      url: productUrl,
      image: uniqueImageUrls.length > 0 ? uniqueImageUrls : undefined,
      publisher: {
        "@type": "Organization",
        "@id": ORGANIZATION_ID,
        name: "Parish Audio",
        url: "https://parishaudio.com",
      },
      brand: {
        "@type": "Brand",
        name: "Parish Audio",
      },
      downloadUrl: [
        productData.windowsDownloadURI
          ? `https://parishaudio.com${productData.windowsDownloadURI}`
          : null,
        productData.macDownloadURI
          ? `https://parishaudio.com${productData.macDownloadURI}`
          : null,
      ].filter(Boolean),
      featureList: bulletPoints.length > 0 ? bulletPoints : undefined,
      offers: {
        "@id": offerId,
      },
      aggregateRating:
        Number(productData.ratingCount) > 0
          ? {
              "@type": "AggregateRating",
              ratingValue: Number(productData.starRating),
              reviewCount: Number(productData.ratingCount),
              bestRating: 5,
              worstRating: 1,
            }
          : undefined,
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": breadcrumbId,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://parishaudio.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Plugins",
          item: "https://parishaudio.com/plugins",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: productData.name,
          item: productUrl,
        },
      ],
    };

    const graph = [productSchema, softwareApplicationSchema, breadcrumbSchema];
    const cleanedGraph = JSON.parse(JSON.stringify(graph));

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(cleanedGraph);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [productData]);

  return null;
};

export default ProductSchemaMarkup;

// import React, { useEffect } from "react";
// import {
//   ORGANIZATION_ID,
//   buildInstantDownloadShippingDetails,
//   buildReturnPolicyReference,
// } from "./../../components/SchemaHelpers.js";

// const ProductSchemaMarkup = ({ productData }) => {
//   useEffect(() => {
//     if (!productData) return;

//     const productUrl = `https://parishaudio.com/products/${productData.productURI}`;
//     const productId = `${productUrl}#product`;
//     const offerId = `${productUrl}#offer`;
//     const appId = `${productUrl}#app`;
//     const breadcrumbId = `${productUrl}#breadcrumb`;

//     const imageUrls = [
//       productData.googleMarketplaceImage
//         ? `https://parishaudio.com${productData.googleMarketplaceImage}`
//         : null,
//       ...(productData.images || [])
//         .filter((img) => img && img.URI)
//         .map((img) => `https://parishaudio.com${img.URI}`),
//     ].filter(Boolean);

//     const uniqueImageUrls = [...new Set(imageUrls)];

//     const bulletPoints = [
//       productData.bulletPoint1,
//       productData.bulletPoint2,
//       productData.bulletPoint3,
//       productData.bulletPoint4,
//       productData.bulletPoint5,
//     ].filter(Boolean);

//     const controls = (productData.controls || []).map((control) => ({
//       "@type": "PropertyValue",
//       name: control.name,
//       value: control.description,
//     }));

//     const additionalProperties = [
//       ...bulletPoints.map((point, index) => ({
//         "@type": "PropertyValue",
//         name: `Feature ${index + 1}`,
//         value: point,
//       })),
//       ...controls,
//       ...(productData.windowsDownloadURI
//         ? [
//             {
//               "@type": "PropertyValue",
//               name: "Windows Download URL",
//               value: `https://parishaudio.com${productData.windowsDownloadURI}`,
//             },
//           ]
//         : []),
//       ...(productData.macDownloadURI
//         ? [
//             {
//               "@type": "PropertyValue",
//               name: "Mac Download URL",
//               value: `https://parishaudio.com${productData.macDownloadURI}`,
//             },
//           ]
//         : []),
//       {
//         "@type": "PropertyValue",
//         name: "Plugin Format",
//         value: "VST3",
//       },
//     ];

//     const price = Number(productData.price || 0).toFixed(2);
//     const msrp =
//       productData.msrp !== undefined &&
//       productData.msrp !== null &&
//       productData.msrp !== ""
//         ? Number(productData.msrp).toFixed(2)
//         : null;

//     const productSchema = {
//       "@context": "https://schema.org",
//       "@type": "Product",
//       "@id": productId,
//       name: productData.name,
//       description:
//         productData.longDescription ||
//         productData.shortDescription ||
//         productData.cardSummary ||
//         undefined,
//       image: uniqueImageUrls.length > 0 ? uniqueImageUrls : undefined,
//       sku: productData.SKU || undefined,
//       category: "Audio Plugin",
//       url: productUrl,
//       brand: {
//         "@type": "Brand",
//         name: "Parish Audio",
//       },
//       manufacturer: {
//         "@type": "Organization",
//         "@id": ORGANIZATION_ID,
//         name: "Parish Audio",
//         url: "https://parishaudio.com",
//       },
//       isRelatedTo: {
//         "@id": appId,
//       },
//       additionalProperty:
//         additionalProperties.length > 0 ? additionalProperties : undefined,
//       offers: {
//         "@type": "Offer",
//         "@id": offerId,
//         url: productUrl,
//         priceCurrency: "USD",
//         price,
//         availability: "https://schema.org/InStock",
//         itemCondition: "https://schema.org/NewCondition",
//         seller: {
//           "@type": "Organization",
//           "@id": ORGANIZATION_ID,
//           name: "Parish Audio",
//           url: "https://parishaudio.com",
//         },
//         hasMerchantReturnPolicy: buildReturnPolicyReference(),
//         shippingDetails: buildInstantDownloadShippingDetails("USD"),
//         ...(msrp && msrp !== price
//           ? {
//               priceSpecification: [
//                 {
//                   "@type": "UnitPriceSpecification",
//                   priceCurrency: "USD",
//                   price: msrp,
//                   priceType: "https://schema.org/ListPrice",
//                 },
//                 {
//                   "@type": "UnitPriceSpecification",
//                   priceCurrency: "USD",
//                   price,
//                   priceType: "https://schema.org/SalePrice",
//                 },
//               ],
//             }
//           : {}),
//       },
//       aggregateRating:
//         Number(productData.ratingCount) > 0
//           ? {
//               "@type": "AggregateRating",
//               ratingValue: Number(productData.starRating),
//               reviewCount: Number(productData.ratingCount),
//               bestRating: 5,
//               worstRating: 1,
//             }
//           : undefined,
//     };

//     const softwareApplicationSchema = {
//       "@context": "https://schema.org",
//       "@type": "SoftwareApplication",
//       "@id": appId,
//       name: productData.name,
//       description:
//         productData.longDescription ||
//         productData.shortDescription ||
//         productData.cardSummary ||
//         undefined,
//       applicationCategory: "MultimediaApplication",
//       operatingSystem: ["Windows", "macOS"],
//       softwareVersion: productData.productVersion || "1.0",
//       url: productUrl,
//       image: uniqueImageUrls.length > 0 ? uniqueImageUrls : undefined,
//       publisher: {
//         "@type": "Organization",
//         "@id": ORGANIZATION_ID,
//         name: "Parish Audio",
//         url: "https://parishaudio.com",
//       },
//       brand: {
//         "@type": "Brand",
//         name: "Parish Audio",
//       },
//       downloadUrl: [
//         productData.windowsDownloadURI
//           ? `https://parishaudio.com${productData.windowsDownloadURI}`
//           : null,
//         productData.macDownloadURI
//           ? `https://parishaudio.com${productData.macDownloadURI}`
//           : null,
//       ].filter(Boolean),
//       featureList: bulletPoints.length > 0 ? bulletPoints : undefined,
//       offers: {
//         "@id": offerId,
//       },
//       aggregateRating:
//         Number(productData.ratingCount) > 0
//           ? {
//               "@type": "AggregateRating",
//               ratingValue: Number(productData.starRating),
//               reviewCount: Number(productData.ratingCount),
//               bestRating: 5,
//               worstRating: 1,
//             }
//           : undefined,
//     };

//     const breadcrumbSchema = {
//       "@context": "https://schema.org",
//       "@type": "BreadcrumbList",
//       "@id": breadcrumbId,
//       itemListElement: [
//         {
//           "@type": "ListItem",
//           position: 1,
//           name: "Home",
//           item: "https://parishaudio.com/",
//         },
//         {
//           "@type": "ListItem",
//           position: 2,
//           name: "Plugins",
//           item: "https://parishaudio.com/plugins",
//         },
//         {
//           "@type": "ListItem",
//           position: 3,
//           name: productData.name,
//           item: productUrl,
//         },
//       ],
//     };

//     const graph = [productSchema, softwareApplicationSchema, breadcrumbSchema];
//     const cleanedGraph = JSON.parse(JSON.stringify(graph));

//     const script = document.createElement("script");
//     script.type = "application/ld+json";
//     script.text = JSON.stringify(cleanedGraph);
//     document.head.appendChild(script);

//     return () => {
//       document.head.removeChild(script);
//     };
//   }, [productData]);

//   return null;
// };

// export default ProductSchemaMarkup;

// import React, { useEffect } from "react";

// const ProductSchemaMarkup = ({ productData }) => {
//   useEffect(() => {
//     if (!productData) return;

//     const productUrl = `https://parishaudio.com/products/${productData.productURI}`;
//     const productId = `${productUrl}#product`;
//     const offerId = `${productUrl}#offer`;
//     const appId = `${productUrl}#app`;
//     const breadcrumbId = `${productUrl}#breadcrumb`;

//     const imageUrls = [
//       productData.googleMarketplaceImage
//         ? `https://parishaudio.com${productData.googleMarketplaceImage}`
//         : null,
//       ...(productData.images || [])
//         .filter((img) => img && img.URI)
//         .map((img) => `https://parishaudio.com${img.URI}`),
//     ].filter(Boolean);

//     const uniqueImageUrls = [...new Set(imageUrls)];

//     const bulletPoints = [
//       productData.bulletPoint1,
//       productData.bulletPoint2,
//       productData.bulletPoint3,
//       productData.bulletPoint4,
//       productData.bulletPoint5,
//     ].filter(Boolean);

//     const controls = (productData.controls || []).map((control) => ({
//       "@type": "PropertyValue",
//       name: control.name,
//       value: control.description,
//     }));

//     const additionalProperties = [
//       ...bulletPoints.map((point, index) => ({
//         "@type": "PropertyValue",
//         name: `Feature ${index + 1}`,
//         value: point,
//       })),
//       ...controls,
//       ...(productData.windowsDownloadURI
//         ? [
//             {
//               "@type": "PropertyValue",
//               name: "Windows Download URL",
//               value: `https://parishaudio.com${productData.windowsDownloadURI}`,
//             },
//           ]
//         : []),
//       ...(productData.macDownloadURI
//         ? [
//             {
//               "@type": "PropertyValue",
//               name: "Mac Download URL",
//               value: `https://parishaudio.com${productData.macDownloadURI}`,
//             },
//           ]
//         : []),
//       {
//         "@type": "PropertyValue",
//         name: "Plugin Format",
//         value: "VST3",
//       },
//     ];

//     const price = Number(productData.price || 0).toFixed(2);
//     const msrp =
//       productData.msrp !== undefined &&
//       productData.msrp !== null &&
//       productData.msrp !== ""
//         ? Number(productData.msrp).toFixed(2)
//         : null;

//     const productSchema = {
//       "@context": "https://schema.org",
//       "@type": "Product",
//       "@id": productId,
//       name: productData.name,
//       description:
//         productData.longDescription ||
//         productData.shortDescription ||
//         productData.cardSummary ||
//         undefined,
//       image: uniqueImageUrls.length > 0 ? uniqueImageUrls : undefined,
//       sku: productData.SKU || undefined,
//       category: "Audio Plugin",
//       url: productUrl,
//       brand: {
//         "@type": "Brand",
//         name: "Parish Audio",
//       },
//       manufacturer: {
//         "@type": "Organization",
//         name: "Parish Audio",
//         url: "https://parishaudio.com",
//       },
//       isRelatedTo: {
//         "@id": appId,
//       },
//       additionalProperty:
//         additionalProperties.length > 0 ? additionalProperties : undefined,
//       offers: {
//         "@type": "Offer",
//         "@id": offerId,
//         url: productUrl,
//         priceCurrency: "USD",
//         price,
//         availability: "https://schema.org/InStock",
//         itemCondition: "https://schema.org/NewCondition",
        
//         seller: {
//           "@type": "Organization",
//           name: "Parish Audio",
//           url: "https://parishaudio.com",
//         },
//         hasMerchantReturnPolicy: {
//           "@type": "MerchantReturnPolicy",
//           returnPolicyCategory:
//             "https://schema.org/MerchantReturnNotPermitted",
//           merchantReturnLink: "https://parishaudio.com/return-policy",
//         },
//         shippingDetails: {
//           "@type": "OfferShippingDetails",
//           hasShippingService: {
//             "@id": "https://parishaudio.com/#shipping-policy",
//           },
//         },
//         ...(msrp && msrp !== price
//           ? {
//               priceSpecification: [
//                 {
//                   "@type": "UnitPriceSpecification",
//                   priceCurrency: "USD",
//                   price: msrp,
//                   priceType: "https://schema.org/ListPrice",
//                 },
//                 {
//                   "@type": "UnitPriceSpecification",
//                   priceCurrency: "USD",
//                   price,
//                   priceType: "https://schema.org/SalePrice",
//                 },
//               ],
//             }
//           : {}),
//       },
//       aggregateRating:
//         Number(productData.ratingCount) > 0
//           ? {
//               "@type": "AggregateRating",
//               ratingValue: Number(productData.starRating),
//               reviewCount: Number(productData.ratingCount),
//               bestRating: 5,
//               worstRating: 1,
//             }
//           : undefined,
//     };

//     const softwareApplicationSchema = {
//       "@context": "https://schema.org",
//       "@type": "SoftwareApplication",
//       "@id": appId,
//       name: productData.name,
//       description:
//         productData.longDescription ||
//         productData.shortDescription ||
//         productData.cardSummary ||
//         undefined,
//       applicationCategory: "MultimediaApplication",
//       operatingSystem: ["Windows", "macOS"],
//       softwareVersion: productData.productVersion || "1.0",
//       url: productUrl,
//       image: uniqueImageUrls.length > 0 ? uniqueImageUrls : undefined,
//       publisher: {
//         "@type": "Organization",
//         name: "Parish Audio",
//         url: "https://parishaudio.com",
//       },
//       brand: {
//         "@type": "Brand",
//         name: "Parish Audio",
//       },
//       downloadUrl: [
//         productData.windowsDownloadURI
//           ? `https://parishaudio.com${productData.windowsDownloadURI}`
//           : null,
//         productData.macDownloadURI
//           ? `https://parishaudio.com${productData.macDownloadURI}`
//           : null,
//       ].filter(Boolean),
//       featureList: bulletPoints.length > 0 ? bulletPoints : undefined,
//       offers: {
//         "@id": offerId,
//       },
//       aggregateRating:
//         Number(productData.ratingCount) > 0
//           ? {
//               "@type": "AggregateRating",
//               ratingValue: Number(productData.starRating),
//               reviewCount: Number(productData.ratingCount),
//               bestRating: 5,
//               worstRating: 1,
//             }
//           : undefined,
//     };

//     const breadcrumbSchema = {
//       "@context": "https://schema.org",
//       "@type": "BreadcrumbList",
//       "@id": breadcrumbId,
//       itemListElement: [
//         {
//           "@type": "ListItem",
//           position: 1,
//           name: "Home",
//           item: "https://parishaudio.com/",
//         },
//         {
//           "@type": "ListItem",
//           position: 2,
//           name: "Plugins",
//           item: "https://parishaudio.com/plugins",
//         },
//         {
//           "@type": "ListItem",
//           position: 3,
//           name: productData.name,
//           item: productUrl,
//         },
//       ],
//     };

//     const graph = [productSchema, softwareApplicationSchema, breadcrumbSchema];
//     const cleanedGraph = JSON.parse(JSON.stringify(graph));

//     const script = document.createElement("script");
//     script.type = "application/ld+json";
//     script.text = JSON.stringify(cleanedGraph);
//     document.head.appendChild(script);

//     return () => {
//       document.head.removeChild(script);
//     };
//   }, [productData]);

//   return null;
// };

// export default ProductSchemaMarkup;

// import React, { useEffect } from "react";

// const ProductSchemaMarkup = ({ productData }) => {
//   useEffect(() => {
//     if (!productData) return;

//     const productUrl = `https://parishaudio.com/products/${productData.productURI}`;
//     const productId = `${productUrl}#product`;
//     const offerId = `${productUrl}#offer`;
//     const appId = `${productUrl}#app`;

//     const imageUrls = [
//       productData.googleMarketplaceImage
//         ? `https://parishaudio.com${productData.googleMarketplaceImage}`
//         : null,
//       ...(productData.images || [])
//         .filter((img) => img && img.URI)
//         .map((img) => `https://parishaudio.com${img.URI}`),
//     ].filter(Boolean);

//     const uniqueImageUrls = [...new Set(imageUrls)];

//     const bulletPoints = [
//       productData.bulletPoint1,
//       productData.bulletPoint2,
//       productData.bulletPoint3,
//       productData.bulletPoint4,
//       productData.bulletPoint5,
//     ].filter(Boolean);

//     const controls = (productData.controls || []).map((control) => ({
//       "@type": "PropertyValue",
//       name: control.name,
//       value: control.description,
//     }));

//     const price = Number(productData.price || 0).toFixed(2);
//     const msrp =
//       productData.msrp !== undefined && productData.msrp !== null && productData.msrp !== ""
//         ? Number(productData.msrp).toFixed(2)
//         : null;

//     const productSchema = {
//       "@context": "https://schema.org",
//       "@type": "Product",
//       "@id": productId,
//       name: productData.name,
//       description:
//         productData.longDescription ||
//         productData.shortDescription ||
//         productData.cardSummary ||
//         undefined,
//       image: uniqueImageUrls.length > 0 ? uniqueImageUrls : undefined,
//       sku: productData.SKU || undefined,
//       category: "Audio Plugin",
//       brand: {
//         "@type": "Brand",
//         name: "Parish Audio",
//       },
//       manufacturer: {
//         "@type": "Organization",
//         name: "Parish Audio",
//         url: "https://parishaudio.com",
//       },
//       url: productUrl,
//       isRelatedTo: {
//         "@id": appId,
//       },
//       additionalProperty: [
//         ...(bulletPoints.length > 0
//           ? bulletPoints.map((point, index) => ({
//               "@type": "PropertyValue",
//               name: `Feature ${index + 1}`,
//               value: point,
//             }))
//           : []),
//         ...controls,
//         ...(productData.windowsDownloadURI
//           ? [
//               {
//                 "@type": "PropertyValue",
//                 name: "Windows Download URL",
//                 value: `https://parishaudio.com${productData.windowsDownloadURI}`,
//               },
//             ]
//           : []),
//         ...(productData.macDownloadURI
//           ? [
//               {
//                 "@type": "PropertyValue",
//                 name: "Mac Download URL",
//                 value: `https://parishaudio.com${productData.macDownloadURI}`,
//               },
//             ]
//           : []),
//       ],
//       offers: {
//         "@type": "Offer",
//         "@id": offerId,
//         url: productUrl,
//         priceCurrency: "USD",
//         price,
//         availability: "https://schema.org/InStock",
//         itemCondition: "https://schema.org/NewCondition",
//         seller: {
//           "@type": "Organization",
//           name: "Parish Audio",
//           url: "https://parishaudio.com",
//         },
//         ...(msrp && msrp !== price
//           ? {
//               priceSpecification: [
//                 {
//                   "@type": "UnitPriceSpecification",
//                   priceCurrency: "USD",
//                   price: msrp,
//                   priceType: "https://schema.org/ListPrice",
//                 },
//                 {
//                   "@type": "UnitPriceSpecification",
//                   priceCurrency: "USD",
//                   price,
//                   priceType: "https://schema.org/SalePrice",
//                 },
//               ],
//             }
//           : {}),
//         hasMerchantReturnPolicy: {
//           "@type": "MerchantReturnPolicy",
//           applicableCountry: "US",
//           returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
//           merchantReturnDays: 14,
//           returnMethod: "https://schema.org/ReturnByMail",
//           merchantReturnLink: "https://parishaudio.com/return-policy",
//           returnFees: "https://schema.org/FreeReturn",
//         },
//       },
//       aggregateRating:
//         Number(productData.ratingCount) > 0
//           ? {
//               "@type": "AggregateRating",
//               ratingValue: Number(productData.starRating),
//               reviewCount: Number(productData.ratingCount),
//             }
//           : undefined,
//     };

//     const softwareApplicationSchema = {
//       "@context": "https://schema.org",
//       "@type": "SoftwareApplication",
//       "@id": appId,
//       name: productData.name,
//       description:
//         productData.longDescription ||
//         productData.shortDescription ||
//         productData.cardSummary ||
//         undefined,
//       applicationCategory: "MultimediaApplication",
//       operatingSystem: ["Windows", "macOS"],
//       softwareVersion: productData.productVersion || "1.0",
//       url: productUrl,
//       downloadUrl: [
//         productData.windowsDownloadURI
//           ? `https://parishaudio.com${productData.windowsDownloadURI}`
//           : null,
//         productData.macDownloadURI
//           ? `https://parishaudio.com${productData.macDownloadURI}`
//           : null,
//       ].filter(Boolean),
//       publisher: {
//         "@type": "Organization",
//         name: "Parish Audio",
//         url: "https://parishaudio.com",
//       },
//       brand: {
//         "@type": "Brand",
//         name: "Parish Audio",
//       },
//       offers: {
//         "@id": offerId,
//       },
//       image: uniqueImageUrls.length > 0 ? uniqueImageUrls : undefined,
//       aggregateRating:
//         Number(productData.ratingCount) > 0
//           ? {
//               "@type": "AggregateRating",
//               ratingValue: Number(productData.starRating),
//               reviewCount: Number(productData.ratingCount),
//             }
//           : undefined,
//       featureList: bulletPoints.length > 0 ? bulletPoints : undefined,
//     };

//     const breadcrumbSchema = {
//       "@context": "https://schema.org",
//       "@type": "BreadcrumbList",
//       itemListElement: [
//         {
//           "@type": "ListItem",
//           position: 1,
//           name: "Home",
//           item: "https://parishaudio.com/",
//         },
//         {
//           "@type": "ListItem",
//           position: 2,
//           name: "Plugins",
//           item: "https://parishaudio.com/plugins",
//         },
//         {
//           "@type": "ListItem",
//           position: 3,
//           name: productData.name,
//           item: productUrl,
//         },
//       ],
//     };

//     const graph = [
//       productSchema,
//       softwareApplicationSchema,
//       breadcrumbSchema,
//     ];

//     const cleanedGraph = JSON.parse(JSON.stringify(graph));

//     const script = document.createElement("script");
//     script.type = "application/ld+json";
//     script.text = JSON.stringify(cleanedGraph);
//     document.head.appendChild(script);

//     return () => {
//       document.head.removeChild(script);
//     };
//   }, [productData]);

//   return null;
// };

// export default ProductSchemaMarkup;
